import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useRevealListingKey } from '@/hooks/useListingKey';
import { toast } from '@/hooks/useToast';
import type { Merchant } from '@/lib/merchants';
import {
  Store,
  Check,
  X,
  Loader2,
  Globe,
  MapPin,
  ExternalLink,
  KeyRound,
  Copy,
  CheckCheck,
} from 'lucide-react';

interface DirectListingReviewCardProps {
  listing: Merchant;
  approved: boolean;
  onApprove: (pubkey: string) => Promise<void>;
  onReject: (pubkey: string) => Promise<void>;
  isSubmitting: boolean;
}

export function DirectListingReviewCard({
  listing,
  approved,
  onApprove,
  onReject,
  isSubmitting,
}: DirectListingReviewCardProps) {
  const npub = nip19.npubEncode(listing.pubkey);
  const { mutateAsync: revealKey, isPending: revealing } = useRevealListingKey();
  const [revealedNsec, setRevealedNsec] = useState('');
  const [copied, setCopied] = useState(false);

  const handleReveal = async () => {
    try {
      const nsec = await revealKey(listing.pubkey);
      setRevealedNsec(nsec);
    } catch (e) {
      toast({
        title: 'Could not reveal key',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const copyNsec = async () => {
    try {
      await navigator.clipboard.writeText(revealedNsec);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Card className={approved ? '' : 'border-amber-300/50 bg-amber-50/30 dark:border-amber-700/30 dark:bg-amber-950/10'}>
      <CardContent className="py-4 px-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center size-10 rounded-lg bg-bch/10 shrink-0">
            <Store className="size-5 text-bch" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate">{listing.name}</span>
              {approved ? (
                <Badge className="text-[10px] py-0 px-1.5 bg-bch/10 text-bch">Approved</Badge>
              ) : (
                <Badge className="text-[10px] py-0 px-1.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  Pending
                </Badge>
              )}
            </div>
            {listing.summary && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{listing.summary}</p>
            )}
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
              {listing.location ? (
                <span className="flex items-center gap-1">
                  <MapPin className="size-3" /> {listing.geohash || 'Mapped'}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Globe className="size-3" /> Online only
                </span>
              )}
              {listing.categories[0] && <span>· {listing.categories[0]}</span>}
            </div>
            <a
              href={`https://bchnostr.com/profile/${npub}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground font-mono hover:text-foreground inline-flex items-center gap-1 mt-1"
            >
              {npub.slice(0, 14)}…{npub.slice(-6)}
              <ExternalLink className="size-3" />
            </a>
          </div>
        </div>

        {/* Recovery key (admin-only, decrypted on demand) */}
        {revealedNsec ? (
          <div className="rounded-lg bg-muted/40 px-3 py-2 space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <KeyRound className="size-3 text-amber-500" />
              Recovery key (keep private)
            </div>
            <div className="flex items-center gap-2">
              <Input readOnly value={revealedNsec} className="font-mono text-xs" />
              <Button variant="outline" size="icon" className="shrink-0" onClick={copyNsec} aria-label="Copy recovery key">
                {copied ? <CheckCheck className="size-4 text-bch" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground hover:text-foreground"
            onClick={handleReveal}
            disabled={revealing}
          >
            {revealing ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : <KeyRound className="size-3.5 mr-1.5" />}
            Reveal recovery key
          </Button>
        )}

        <div className="flex gap-2">
          {approved ? (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-destructive hover:text-destructive"
              disabled={isSubmitting}
              onClick={() => onReject(listing.pubkey)}
            >
              {isSubmitting ? <Loader2 className="size-4 mr-1.5 animate-spin" /> : <X className="size-4 mr-1.5" />}
              Remove from map
            </Button>
          ) : (
            <Button
              size="sm"
              className="flex-1 bg-bch hover:bg-bch/90 text-bch-foreground"
              disabled={isSubmitting}
              onClick={() => onApprove(listing.pubkey)}
            >
              {isSubmitting ? <Loader2 className="size-4 mr-1.5 animate-spin" /> : <Check className="size-4 mr-1.5" />}
              Approve &amp; verify
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
